import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/shared/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/shared/components/ui/dropdown-menu";
import { Button } from "@/shared/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/shared/components/ui/tooltip";
import { useTranslation } from "react-i18next";
import { MoreVertical, Info, X, Repeat, AlertTriangle } from "lucide-react";
import { ScheduleService } from "@/features/Assistant/services/scheduleService";
import type { UpcomingMeeting } from "@/features/Assistant/types/schedule";

interface UpcomingMeetingsTableProps {
  meetings: UpcomingMeeting[];
  totalPages: number;
  page: number;
  setPage: (p: number) => void;
  rowsPerPage: string;
  setRowsPerPage: (v: string) => void;
  sortField: string;
  sortAsc: boolean;
  onSort: (field: string) => void;
  getSortIcon: (field: string, currentField: string, isAsc: boolean) => string | null;
  /** Occurrences per series inside the read window — derived from the calendar. */
  seriesOccurrences: Map<string, number>;
  onDetails: (meeting: UpcomingMeeting) => void;
  onBlockMeeting: (id: string) => void;
  onBlockSeries: (seriesId: string) => void;
}

export const UpcomingMeetingsTable = ({
  meetings,
  totalPages,
  page,
  setPage,
  rowsPerPage,
  setRowsPerPage,
  sortField,
  sortAsc,
  onSort,
  getSortIcon,
  seriesOccurrences,
  onDetails,
  onBlockMeeting,
  onBlockSeries,
}: UpcomingMeetingsTableProps) => {
  const { t } = useTranslation();

  /**
   * A real button, so sorting is reachable by keyboard and announced by a screen reader —
   * a clickable `<th>` is neither.
   */
  const SortButton = ({ field, label }: { field: string; label: string }) => (
    <button
      type="button"
      onClick={() => onSort(field)}
      aria-label={label}
      className="inline-flex items-center gap-1 uppercase hover:text-foreground"
    >
      {label} {getSortIcon(field, sortField, sortAsc)}
    </button>
  );

  return (
    <div className="space-y-4">
      {meetings.length > 0 ? (
        <div className="rounded-lg border bg-card">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead className="text-xs uppercase">
                  <SortButton field="fromEmail" label={t("assistant.schedule.columns.from")} />
                </TableHead>
                <TableHead className="text-xs uppercase">
                  <SortButton field="meetingName" label={t("assistant.schedule.columns.meeting")} />
                </TableHead>
                <TableHead className="text-xs uppercase">{t("assistant.schedule.columns.workspace")}</TableHead>
                <TableHead className="text-xs uppercase">{t("assistant.schedule.columns.status")}</TableHead>
                <TableHead className="text-xs uppercase">
                  <SortButton field="meetingTime" label={t("assistant.schedule.columns.scheduled")} />
                </TableHead>
                <TableHead className="text-xs uppercase text-right">{t("assistant.schedule.columns.actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {meetings.map((meeting) => {
                // Narrowed once and reused: the series actions take a plain string, so no
                // call site needs a non-null assertion.
                const seriesId = meeting.seriesId;
                const occurrences = seriesId ? seriesOccurrences.get(seriesId) ?? 0 : 0;
                return (
                  <TableRow
                    key={meeting.id}
                    className={meeting.reason ? "bg-destructive/5 hover:bg-destructive/10" : "hover:bg-muted/50"}
                  >
                    <TableCell className="text-xs text-muted-foreground max-w-[220px] truncate">
                      {meeting.fromEmail ?? "—"}
                    </TableCell>
                    <TableCell className="text-sm font-medium">
                      <span className="inline-flex items-center gap-2">
                        {meeting.meetingName ?? t("assistant.schedule.untitledMeeting")}
                        {seriesId && (
                          <span
                            title={t("assistant.schedule.seriesTooltip", { count: occurrences })}
                            className="inline-flex items-center gap-1 rounded-full border border-border bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground"
                          >
                            <Repeat className="h-3 w-3" />
                            {t("assistant.schedule.series")}
                          </span>
                        )}
                      </span>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-[160px] truncate">
                      {meeting.workspace ?? "—"}
                    </TableCell>
                    <TableCell className="text-xs">
                      {meeting.reason ? (
                        <span className="inline-flex items-center gap-1.5">
                          <span className="inline-flex items-center gap-1.5 rounded-full border border-destructive/30 bg-destructive/10 px-2 py-0.5 font-medium text-destructive">
                            <AlertTriangle className="h-3 w-3" />
                            {t("assistant.schedule.statuses.rejected")}
                          </span>
                          {/* O motivo sai do rótulo e vem para cá: a coluna passa a
                              responder "atende ou não" de bate-pronto, e o porquê fica
                              a um hover de distância, sem alargar a tabela. */}
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                type="button"
                                aria-label={t("assistant.schedule.a11y.rejectionReason")}
                                className="text-destructive/70 transition-colors hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-full"
                              >
                                <Info className="h-3.5 w-3.5" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                              <p className="font-medium">
                                {t(`assistant.schedule.reasons.${meeting.reason}`, {
                                  defaultValue: t("assistant.schedule.reasons.unknown"),
                                })}
                              </p>
                              <p className="text-muted-foreground">
                                {t(`assistant.schedule.reasonHelp.${meeting.reason}`, {
                                  defaultValue: t("assistant.schedule.reasonHelp.unknown"),
                                })}
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </span>
                      ) : (
                        <span className="inline-flex items-center rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 font-medium text-emerald-600 dark:text-emerald-400">
                          {t("assistant.schedule.statuses.ready")}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {ScheduleService.formatTimeRange(meeting.meetingTime ?? '', meeting.meetingEndTime ?? undefined)}
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            aria-label={t("assistant.schedule.a11y.openMenu")}
                          >
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => onDetails(meeting)}>
                            <Info className="mr-2 h-4 w-4" />
                            {t("assistant.schedule.actions.details")}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => onBlockMeeting(meeting.id)}
                            className="text-red-500 focus:text-red-500 data-[highlighted]:text-red-500 data-[highlighted]:bg-red-500/10 font-medium cursor-pointer"
                          >
                            <X className="mr-2 h-4 w-4 !opacity-100 text-red-500" />
                            {t("assistant.schedule.actions.blockMeeting")}
                          </DropdownMenuItem>
                          {seriesId && (
                            <DropdownMenuItem
                              onClick={() => onBlockSeries(seriesId)}
                              className="text-red-500 focus:text-red-500 data-[highlighted]:text-red-500 data-[highlighted]:bg-red-500/10 font-medium cursor-pointer"
                            >
                              <X className="mr-2 h-4 w-4 !opacity-100 text-red-500" />
                              {t("assistant.schedule.actions.blockSeries")}
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>

          <div className="flex items-center justify-between px-4 py-2 border-t text-xs text-muted-foreground">
            <div className="flex items-center gap-2">
              <span>{t("assistant.schedule.rowsPerPage")}</span>
              <Select value={rowsPerPage} onValueChange={(value) => { setRowsPerPage(value); setPage(1); }}>
                <SelectTrigger className="w-16 h-7">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page <= 1}
                className="h-7 w-7 p-0"
                aria-label={t("assistant.schedule.a11y.previousPage")}
              >
                ‹
              </Button>
              <span>{t("assistant.schedule.pagination", { current: page, total: totalPages })}</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page >= totalPages}
                className="h-7 w-7 p-0"
                aria-label={t("assistant.schedule.a11y.nextPage")}
              >
                ›
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="py-12 text-center border rounded-lg bg-muted/20">
          <p className="text-sm text-muted-foreground">
            {t("assistant.schedule.noUpcomingMeetings")}
          </p>
        </div>
      )}
    </div>
  );
};
